<template>
  <div class="income-container">
    <!-- 页面标题 -->
    <div class="page-header">
      <h2 class="page-title">
        <i class="el-icon-data-analysis"></i>
        平台收入排行榜
      </h2>
      <p class="page-subtitle">平台整体收入统计与分析</p>
    </div>

    <!-- 统计卡片 -->
    <el-row :gutter="24" class="stats-row">
      <el-col :xs="24" :sm="8">
        <div class="stat-card">
          <div class="stat-icon" style="background: linear-gradient(135deg, #FF7A5C, #f5222d);">
            <i class="el-icon-office-building"></i>
          </div>
          <div class="stat-content">
            <div class="stat-label">总团队数</div>
            <div class="stat-value">{{ incomeList.length }}</div>
          </div>
        </div>
      </el-col>
      <el-col :xs="24" :sm="8">
        <div class="stat-card">
          <div class="stat-icon" style="background: linear-gradient(135deg, #722ed1, #531dab);">
            <i class="el-icon-coin"></i>
          </div>
          <div class="stat-content">
            <div class="stat-label">平台总收入</div>
            <div class="stat-value">￥{{ totalIncome.toFixed(2) }}</div>
          </div>
        </div>
      </el-col>
      <el-col :xs="24" :sm="8">
        <div class="stat-card">
          <div class="stat-icon" style="background: linear-gradient(135deg, #13c2c2, #08979c);">
            <i class="el-icon-star-on"></i>
          </div>
          <div class="stat-content">
            <div class="stat-label">最高收入</div>
            <div class="stat-value">￥{{ topIncome.toFixed(2) }}</div>
          </div>
        </div>
      </el-col>
    </el-row>

    <!-- 数据表格 -->
    <el-card class="table-card">
      <div slot="header" class="card-header">
        <span><i class="el-icon-pie-chart"></i> 平台收入详情</span>
        <el-button 
          type="danger" 
          size="small" 
          icon="el-icon-refresh" 
          @click="loadData"
          :loading="loading"
          style="background:#FF7A5C;border-color:#FF7A5C;">
          刷新数据
        </el-button>
      </div>

      <el-table 
        :data="incomeList" 
        style="width: 100%" 
        v-loading="loading"
        :default-sort="{prop: 'income', order: 'descending'}">
        <el-table-column type="index" label="排名" width="100" align="center">
          <template slot-scope="scope">
            <div v-if="scope.$index === 0" class="rank-badge rank-1">
              <i class="el-icon-trophy"></i>
              <span>TOP 1</span>
            </div>
            <div v-else-if="scope.$index === 1" class="rank-badge rank-2">
              <i class="el-icon-trophy"></i>
              <span>TOP 2</span>
            </div>
            <div v-else-if="scope.$index === 2" class="rank-badge rank-3">
              <i class="el-icon-trophy"></i>
              <span>TOP 3</span>
            </div>
            <span v-else class="rank-normal">{{ scope.$index + 1 }}</span>
          </template>
        </el-table-column>

        <el-table-column prop="jobName" label="团队/负责人" min-width="180">
          <template slot-scope="scope">
            <div class="team-info">
              <div class="team-avatar">
                <i class="el-icon-s-custom"></i>
              </div>
              <div class="team-details">
                <div class="team-name">{{ scope.row.jobName }}</div>
                <div class="team-id">ID: {{ scope.row.id || 'N/A' }}</div>
              </div>
            </div>
          </template>
        </el-table-column>

        <el-table-column prop="schoolName" label="所属学校" min-width="150" align="center">
          <template slot-scope="scope">
            <el-tag type="warning" size="medium">{{ scope.row.schoolName }}</el-tag>
          </template>
        </el-table-column>

        <el-table-column prop="income" label="平台收入" min-width="150" align="right" sortable>
          <template slot-scope="scope">
            <div class="income-cell">
              <span class="income-amount platform">￥{{ (scope.row.income || 0).toFixed(2) }}</span>
              <el-progress 
                :percentage="getPercentage(scope.row.income || 0)" 
                :color="getPlatformColor(scope.$index)"
                :show-text="false"
                class="income-progress">
              </el-progress>
            </div>
          </template>
        </el-table-column>

        <el-table-column label="占比" width="120" align="center">
          <template slot-scope="scope">
            <el-tag type="info" size="small">
              {{ getIncomePercentage(scope.row.income || 0) }}%
            </el-tag>
          </template>
        </el-table-column>

        <el-table-column label="操作" width="120" align="center" fixed="right">
          <template slot-scope="scope">
            <el-button 
              type="text" 
              size="small" 
              @click="viewDetails(scope.row)"
              style="color: #FF7A5C;">
              <i class="el-icon-data-analysis"></i> 分析
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 空状态 -->
      <div v-if="!loading && incomeList.length === 0" class="empty-state">
        <i class="el-icon-data-board"></i>
        <p>暂无平台收入数据</p>
      </div>
    </el-card>
  </div>
</template>

<script>
export default {
  name: 'PlatformIncome',
  data() {
    return {
      loading: false,
      incomeList: [],
    };
  },
  computed: {
    totalIncome() {
      if (!Array.isArray(this.incomeList)) return 0;
      return this.incomeList.reduce((sum, item) => sum + (item.income || 0), 0);
    },
    topIncome() {
      if (!Array.isArray(this.incomeList) || this.incomeList.length === 0) return 0;
      return Math.max(...this.incomeList.map(item => item.income || 0));
    }
  },
  created() {
    this.loadData();
  },
  methods: {
    async loadData() {
      this.loading = true;
      try {
        const { data: res } = await this.$http.get('/init/getIncomeTotal');
        console.log('平台收入接口返回:', res);
        
        if (res.code === 200) {
          let rawData = res.data;
          let dataArray = [];
          
          // 如果返回的是对象，提取商家收入数据（平台收入）
          if (rawData && typeof rawData === 'object' && !Array.isArray(rawData)) {
            const shopIncome = rawData.shopIncome || [];
            dataArray = shopIncome;
            console.log('提取商家收入数据:', shopIncome.length);
          } else if (Array.isArray(rawData)) {
            dataArray = rawData;
          }
          
          // 格式化数据：平台收入接口返回的是数组的数组 [shopName, schoolName, income]
          this.incomeList = dataArray
            .filter(item => Array.isArray(item) && item.length >= 3)
            .map((item, index) => ({
              id: index + 1,
              jobName: item[0] || '未知',              // 第一个元素：商家名称
              schoolName: item[1] || '未知',           // 第二个元素：学校名称
              income: parseFloat(item[2]) || 0         // 第三个元素：收入金额（字符串转数字）
            }))
            // 按收入从高到低排序
            .sort((a, b) => b.income - a.income);
          
          console.log('处理后的incomeList:', this.incomeList.length + '条', this.incomeList.slice(0, 3));
        } else {
          this.$message.error('加载数据失败：' + res.msg);
          this.incomeList = [];
        }
      } catch (error) {
        console.error('加载平台收入数据失败:', error);
        this.$message.error('加载数据失败，请检查网络连接');
        this.incomeList = [];
      } finally {
        this.loading = false;
      }
    },
    getPercentage(income) {
      if (this.topIncome === 0) return 0;
      return Math.round((income / this.topIncome) * 100);
    },
    getIncomePercentage(income) {
      if (this.totalIncome === 0) return 0;
      return ((income / this.totalIncome) * 100).toFixed(1);
    },
    getPlatformColor(index) {
      if (index === 0) return '#ff4d4f';
      if (index === 1) return '#ff7a45';
      if (index === 2) return '#ffa940';
      return '#FF7A5C';
    },
    viewDetails(row) {
      // 跳转到交易明细页面，并传递团队名称作为查询条件
      this.$router.push({
        path: '/transaction_detail',
        query: {
          shopName: row.jobName,
          schoolName: row.schoolName
        }
      });
    }
  }
};
</script>

<style scoped>
.income-container {
  padding: 20px;
  background: #f5f7fa;
  min-height: calc(100vh - 64px);
}

/* 页面标题 */
.page-header {
  margin-bottom: 24px;
}

.page-title {
  font-size: 24px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 8px 0;
  display: flex;
  align-items: center;
  gap: 8px;
}

.page-title i {
  color: #FF7A5C;
}

.page-subtitle {
  font-size: 14px;
  color: var(--text-secondary);
  margin: 0;
}

/* 统计卡片 */
.stats-row {
  margin-bottom: 24px;
}

.stat-card {
  background: white;
  border-radius: 12px;
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  box-shadow: 0 2px 12px rgba(255, 122, 92, 0.08);
  transition: all 0.3s ease;
  margin-bottom: 20px;
}

.stat-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 24px rgba(255, 122, 92, 0.15);
}

.stat-icon {
  width: 60px;
  height: 60px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
  color: white;
}

.stat-content {
  flex: 1;
}

.stat-label {
  font-size: 14px;
  color: var(--text-secondary);
  margin-bottom: 8px;
}

.stat-value {
  font-size: 24px;
  font-weight: 700;
  color: var(--text-primary);
}

/* 表格卡片 */
.table-card {
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(255, 122, 92, 0.08);
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-weight: 600;
  color: var(--text-primary);
}

.card-header i {
  color: #FF7A5C;
  margin-right: 8px;
}

/* 排名徽章 */
.rank-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 20px;
  font-weight: 600;
  font-size: 14px;
}

.rank-1 {
  background: linear-gradient(135deg, #ff4d4f, #cf1322);
  color: white;
  box-shadow: 0 4px 12px rgba(207, 19, 34, 0.3);
}

.rank-2 {
  background: linear-gradient(135deg, #ff7a45, #d4380d);
  color: white;
  box-shadow: 0 4px 12px rgba(212, 56, 13, 0.3);
}

.rank-3 {
  background: linear-gradient(135deg, #ffa940, #d46b08);
  color: white;
  box-shadow: 0 4px 12px rgba(212, 107, 8, 0.3);
}

.rank-normal {
  font-size: 16px;
  font-weight: 600;
  color: var(--text-secondary);
}

/* 团队信息 */
.team-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.team-avatar {
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: linear-gradient(135deg, #FF7A5C, #f5222d);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 20px;
}

.team-details {
  flex: 1;
}

.team-name {
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 4px;
}

.team-id {
  font-size: 12px;
  color: var(--text-secondary);
}

/* 收入单元格 */
.income-cell {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.income-amount.platform {
  font-size: 16px;
  font-weight: 700;
  color: #FF7A5C;
}

.income-progress {
  width: 100%;
}

/* 空状态 */
.empty-state {
  text-align: center;
  padding: 60px 20px;
  color: var(--text-secondary);
}

.empty-state i {
  font-size: 64px;
  color: var(--border-color);
  margin-bottom: 16px;
}

.empty-state p {
  font-size: 14px;
  margin: 0;
}

/* 响应式 */
@media (max-width: 768px) {
  .stat-card {
    flex-direction: column;
    text-align: center;
  }
  
  .team-info {
    flex-direction: column;
    text-align: center;
  }
}
</style>

